import Keycloak, {
  KeycloakConfig,
  KeycloakInitOptions,
  KeycloakLogoutOptions
} from "keycloak-js";

export interface KeycloakInitConfig {
  issuer: string;
  clientId: string;
  redirectUri?: string;
}

let keycloak: Keycloak | null = null;
let initPromise: Promise<Keycloak> | null = null;

function ensureBrowserContext(): void {
  if (typeof window === "undefined") {
    throw new Error("Keycloak integration requires a browser environment");
  }
}

function resolveBasePath(): string {
  const base = import.meta.env.BASE_URL ?? "/";
  return base.endsWith("/") ? base : `${base}/`;
}

function defaultRedirectUri(): string {
  ensureBrowserContext();
  return `${window.location.origin}${resolveBasePath()}`;
}

function parseIssuer(issuer: string): { baseUrl: string; realm: string } {
  try {
    const url = new URL(issuer);
    const segments = url.pathname.split("/").filter(Boolean);
    const realmIndex = segments.findIndex((segment) => segment === "realms");
    const realmSegment = realmIndex >= 0 ? segments[realmIndex + 1] : undefined;
    if (!realmSegment) {
      throw new Error("Realm segment missing from issuer URL");
    }
    return {
      baseUrl: `${url.protocol}//${url.host}`,
      realm: decodeURIComponent(realmSegment)
    };
  } catch (error) {
    throw new Error(`Invalid Keycloak issuer URL: ${issuer}`);
  }
}

export function getKeycloakInstance(): Keycloak | null {
  return keycloak;
}

export function isKeycloakInitialized(): boolean {
  return keycloak !== null;
}

export function resetKeycloakInstance(): void {
  keycloak = null;
  initPromise = null;
}

export async function initKeycloakClient(config: KeycloakInitConfig): Promise<Keycloak> {
  ensureBrowserContext();

  if (keycloak) {
    return keycloak;
  }
  if (initPromise) {
    return initPromise;
  }

  const { baseUrl, realm } = parseIssuer(config.issuer);
  const keycloakConfig: KeycloakConfig = {
    url: baseUrl,
    realm,
    clientId: config.clientId
  };

  keycloak = new Keycloak(keycloakConfig);
  const redirectUri = config.redirectUri ?? defaultRedirectUri();
  const initOptions: KeycloakInitOptions = {
    onLoad: "login-required",
    checkLoginIframe: false,
    pkceMethod: "S256",
    redirectUri
  };

  initPromise = (async () => {
    try {
      const authenticated = await keycloak!.init(initOptions);
      if (!authenticated) {
        await keycloak!.login({ redirectUri });
      }
      return keycloak!;
    } catch (error) {
      resetKeycloakInstance();
      throw error;
    } finally {
      initPromise = null;
    }
  })();

  return initPromise;
}

export async function keycloakLogout(options?: { redirectUri?: string }): Promise<void> {
  if (!keycloak) {
    return;
  }
  ensureBrowserContext();

  const logoutOptions: KeycloakLogoutOptions = {
    redirectUri: options?.redirectUri ?? defaultRedirectUri()
  };

  try {
    await keycloak.logout(logoutOptions);
  } finally {
    resetKeycloakInstance();
  }
}

export function getAppRedirectUri(): string {
  return defaultRedirectUri();
}
